/* ==========================================================================
   FURLY: Theme JavaScript
   Vanilla + custom elements. No dependencies.
   ========================================================================== */

(function () {
  'use strict';

  const T = window.theme || {};
  const RM = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Liquid's t filter turns an apostrophe into &#39; before json freezes it into a string,
     and toasts print with textContent. Decode once here; a detached textarea runs nothing. */
  if (T.strings) {
    const decoder = document.createElement('textarea');
    Object.keys(T.strings).forEach((key) => {
      const value = T.strings[key];
      if (typeof value !== 'string' || value.indexOf('&') === -1) return;
      decoder.innerHTML = value;
      T.strings[key] = decoder.value;
    });
  }

  /* Shopify lets a shopper put at most 50 of one variant in the cart on this store
     (measured 2026-10-05 on all five variants). Larger numbers are capped on screen. */
  const MAX_PER_VARIANT = 50;
  /* No cart call may hang for ever on a dead mobile connection. */
  const CART_TIMEOUT = 15000;

  /* ------------------------------------------------------------------ utils */

  function formatMoney(cents) {
    /* The shop format may carry HTML once the currency converter app is set up
       ("<span class=money>Rs {{amount}}</span>"); the script wraps prices itself (setMoney),
       so any tags are dropped here and the result is plain store-currency text. */
    const format = ((T.settings && T.settings.moneyFormat) || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas.replace(/,/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas + '.' + parts[1]);
  }

  /* Every price the script writes sits inside <span class="money">, the hook the currency
     converter app (BUCKS) looks for. The text is always the store currency; the app then
     redraws it in the shopper's currency. data-money-base keeps the store-currency text so
     a later copy (the sticky bar) never copies an already converted price. */
  function moneyHtml(text) {
    const safe = String(text == null ? '' : text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    return `<span class="money">${safe}</span>`;
  }

  function setMoney(el, text) {
    if (!el) return;
    el.dataset.moneyBase = text || '';
    el.innerHTML = text ? moneyHtml(text) : '';
    queueReconvert();
  }

  /* One call to the converter app per frame, however many prices changed; nothing happens
     when the app is not installed. */
  let reconvertQueued = false;
  function queueReconvert() {
    if (reconvertQueued) return;
    reconvertQueued = true;
    window.requestAnimationFrame(() => {
      reconvertQueued = false;
      const cc = window.bucksCC;
      if (cc && typeof cc.reConvert === 'function') {
        try { cc.reConvert(); } catch (e) { /* the app reports its own errors */ }
      }
    });
  }

  function trapFocus(container, opener) {
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(container.querySelectorAll(selector)).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    container._releaseFocus = function () {
      container.removeEventListener('keydown', onKeydown);
      if (opener && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    };

    const target = container.querySelector('[data-autofocus]') || container.querySelector(selector);
    /* Retry for a moment: with "reduce motion" on, the drawer can still be
       hidden on the first frame and focus silently stayed behind the overlay. */
    if (target) {
      let tries = 0;
      const tryFocus = () => {
        target.focus({ preventScroll: true });
        if (document.activeElement !== target && tries++ < 8) window.setTimeout(tryFocus, 60);
      };
      window.requestAnimationFrame(tryFocus);
    }
  }

  function releaseFocus(container) {
    if (container && container._releaseFocus) {
      container._releaseFocus();
      container._releaseFocus = null;
    }
  }

  let lockCount = 0;
  function lockScroll(on) {
    lockCount = Math.max(0, lockCount + (on ? 1 : -1));
    document.body.classList.toggle('is-locked', lockCount > 0);
  }

  function toast(message, variant) {
    const host = document.getElementById('ThemeToasts');
    if (!host) return;

    const el = document.createElement('div');
    el.className = 'toast' + (variant === 'error' ? ' toast--error' : '');
    el.setAttribute('role', 'status');
    el.textContent = message;
    host.appendChild(el);

    window.setTimeout(() => {
      el.style.transition = 'opacity 240ms, transform 240ms';
      el.style.opacity = '0';
      el.style.transform = 'translateY(8px)';
      window.setTimeout(() => el.remove(), 260);
    }, 3400);
  }

  /* --------------------------------------------------------------- tracking */

  /* Ad pixels. AddToCart fires here for every add path; the pixel base code
     and PageView/ViewContent live in snippets/tiktok-pixel.liquid and
     snippets/meta-pixel.liquid. Checkout events fire from the custom pixel in
     Shopify Settings > Customer events, because checkout never loads this file. */
  const Track = {
    /* `added` (variant id -> quantity) makes the event carry what was added just now; without
       it the whole line is reported, so a second add counted the first one again. */
    addToCart(cart, variantIds, added) {
      if (!cart || !Array.isArray(cart.items)) return;
      const wanted = variantIds.map(Number);
      cart.items
        .filter((i) => wanted.includes(Number(i.variant_id)))
        .forEach((i) => {
          const id = String(i.variant_id);
          const quantity = added && added[id] ? added[id] : i.quantity;
          const unit = i.quantity ? i.line_price / i.quantity : i.price; /* cents, after any pack deal */
          const value = Math.round(unit * quantity) / 100;
          if (window.ttq) {
            /* TikTok rejects MUR; report in the currency set in theme settings. */
            const fx = window.ttqFx || { currency: cart.currency, rate: 1 };
            const conv = (n) => Math.round((n / fx.rate) * 100) / 100;
            const props = {
              contents: [{
                content_id: id,
                content_type: 'product',
                content_name: i.product_title,
                price: conv(i.price / 100),
                quantity,
                brand: i.vendor || 'Furly'
              }],
              content_type: 'product',
              value: conv(value),
              currency: fx.currency
            };
            /* furlyTT (snippets/tiktok-pixel.liquid) adds the event_id TikTok
               needs to deduplicate against the Events API. */
            if (window.furlyTT) window.furlyTT.track('AddToCart', props);
            else window.ttq.track('AddToCart', props);
          }
          /* Only when snippets/meta-pixel.liquid loaded fbq. The Meta Shopify app
             (when installed) fires its own AddToCart from /cart/add; firing here
             too would double-count. */
          if (window.fbq && window.furlyMetaOwn) {
            window.fbq('track', 'AddToCart', {
              content_ids: [id],
              content_type: 'product',
              content_name: i.product_title,
              value,
              currency: cart.currency
            });
          }
        });
    }
  };

  /* ------------------------------------------------------------ cart events */

  const CartBus = {
    listeners: [],
    subscribe(fn) {
      this.listeners.push(fn);
      return () => {
        this.listeners = this.listeners.filter((l) => l !== fn);
      };
    },
    publish(cart) {
      this.listeners.forEach((fn) => fn(cart));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }
  };

  function refreshCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    document.querySelectorAll('[data-cart-label]').forEach((el) => {
      el.setAttribute('aria-label', cart.item_count > 0 ? `${el.dataset.cartLabel} (${cart.item_count})` : el.dataset.cartLabel);
    });
  }

  /* Every cart call goes through here. A dead connection, a stalled request or an error
     page becomes the theme's translated message, never the browser's own "Failed to fetch"
     or a parser error. `data` is null when the answer was not JSON. */
  async function cartCall(url, options) {
    const controller = 'AbortController' in window ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), CART_TIMEOUT) : null;
    try {
      const res = await fetch(url, controller ? Object.assign({}, options, { signal: controller.signal }) : options);
      let data = null;
      try { data = await res.json(); } catch (e) { data = null; }
      return { res, data };
    } catch (e) {
      throw new Error(T.strings.cartError);
    } finally {
      if (timer) window.clearTimeout(timer);
    }
  }

  async function fetchCart() {
    const { res, data } = await cartCall(`${T.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok || !data) throw new Error(res.status === 429 ? T.strings.cartBusy : T.strings.cartError);
    return data;
  }

  async function fetchCartSections(ids) {
    /* No Accept header here: with "Accept: application/json" Shopify answers /cart?sections=...
       with the cart itself instead of the section HTML. */
    const { res, data } = await cartCall(`${T.routes.cart}?sections=${ids}`, {});
    if (!res.ok || !data) throw new Error(res.status === 429 ? T.strings.cartBusy : T.strings.cartError);
    return data;
  }

  /* Section ids to ask for with a cart request, so the answer already holds the new cart HTML. */
  function cartSectionIds() {
    const ids = [];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    const page = document.querySelector('[data-cart-page-section]');
    if (page && page.dataset.cartPageSection) ids.push(page.dataset.cartPageSection);
    return ids.join(',');
  }

  /* Redraw the cart drawer and, on /cart, the page list. `html` is section HTML that came
     back with a cart request; whatever is missing from it is fetched. */
  async function renderCartSections(cart, html) {
    refreshCartCount(cart);

    const got = Object.assign({}, html || {});
    const drawer = document.querySelector('cart-drawer');
    const cartPage = document.querySelector('[data-cart-page-section]');
    const pageId = cartPage ? cartPage.dataset.cartPageSection : '';
    const missing = [];
    if (drawer && !got['cart-drawer']) missing.push('cart-drawer');
    if (cartPage && pageId && !got[pageId]) missing.push(pageId);
    /* One request for whatever did not come back with the cart call. */
    if (missing.length) Object.assign(got, await fetchCartSections(missing.join(',')));

    if (drawer && got['cart-drawer']) drawer.renderHTML(got['cart-drawer']);
    if (cartPage && got[pageId]) {
      const next = new DOMParser().parseFromString(got[pageId], 'text/html').querySelector('[data-cart-page-section]');
      if (next) {
        const restore = focusKey(cartPage);
        cartPage.innerHTML = next.innerHTML;
        CartLines.paint();
        queueReconvert();
        restoreFocus(cartPage, restore);
      }
    }

    CartBus.publish(cart);
  }

  async function cartRequest(url, body) {
    const { res, data } = await cartCall(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });

    if (!res.ok || !data) {
      /* 422 is Shopify's own stock message, already in the page language. Anything else
         (429 too many tries, a server error, an error page) gets the theme's translated line. */
      const answer = data || {};
      const stock = res.status === 422 ? (answer.description || (typeof answer.errors === 'string' ? answer.errors : '')) : '';
      const error = new Error(stock || (res.status === 429 ? T.strings.cartBusy : '') || T.strings.cartError);
      error.status = res.status;
      throw error;
    }
    return data;
  }

  /* Set an absolute quantity for one variant. Uses add.js for increases so
     Shopify emits product_added_to_cart (the Meta app and Shopify analytics
     only intercept /cart/add; /cart/update.js is invisible to them) and
     change.js for decreases, so repeated clicks never stack. */
  async function setVariantQuantity(variantId, quantity) {
    await CartLines.settle();
    const before = await fetchCart();
    const current = before.items.find((i) => String(i.variant_id) === String(variantId));
    const have = current ? current.quantity : 0;
    /* Other lines already in the cart: the caller shows the cart instead of jumping to payment. */
    const others = before.items.some((i) => String(i.variant_id) !== String(variantId));
    if (have < quantity) {
      /* add.js answers with the line it changed: enough for tracking, no extra round trip. */
      const added = await cartRequest(T.routes.cartAdd, { items: [{ id: Number(variantId), quantity: quantity - have }] });
      /* This answer gives prices as text in rupees ("799.00"); the tracking code expects
         cents like /cart.js (the AddToCart value was 100 times too small). */
      const cents = (v) => (typeof v === 'string' ? Math.round(Number(v) * 100) : v);
      const items = (added.items || []).map((i) => Object.assign({}, i, { price: cents(i.price), line_price: cents(i.line_price) }));
      return { currency: before.currency, items, others, added: { [String(variantId)]: quantity - have } };
    }
    if (have > quantity) await cartRequest(T.routes.cartChange, { id: current.key, quantity });
    /* Nothing was added: no AddToCart event for a lower number or the same pack again. */
    return { currency: before.currency, items: [], others };
  }

  /* After "Buy now" or "Checkout now": straight to payment only when the pack is the whole
     cart. If other lines were already there (an earlier colour, another product), the cart
     opens instead, so nothing is paid for by surprise. Returns true when the page leaves. */
  async function checkoutOrShowCart(result, checkoutUrl, opener) {
    if (!result || !result.others) {
      window.location.href = checkoutUrl;
      return true;
    }
    const cart = await fetchCart();
    await renderCartSections(cart);
    const drawer = document.querySelector('cart-drawer');
    if (T.settings.cartType === 'drawer' && drawer) {
      drawer.open(opener);
      return false;
    }
    window.location.href = T.routes.cart;
    return true;
  }

  window.ThemeCart = {
    async add(items, opener) {
      /* A quantity change still on its way goes first, or the two requests cross. */
      await CartLines.settle();
      const payload = Array.isArray(items) ? { items } : { items: [items] };
      const drawer = document.querySelector('cart-drawer');
      const useDrawer = T.settings.cartType === 'drawer' && drawer;
      const fast = useDrawer && !document.querySelector('[data-cart-page-section]');
      if (fast) payload.sections = 'cart-drawer';
      const added = await cartRequest(T.routes.cartAdd, payload);
      delete payload.sections;
      const addedNow = {};
      payload.items.forEach((i) => { addedNow[String(i.id)] = (addedNow[String(i.id)] || 0) + Number(i.quantity || 1); });

      /* Open the drawer from the HTML that came back with the add; count, tracking and any
         other cart views follow in the background. */
      const html = added && added.sections && added.sections['cart-drawer'];
      if (fast && html) {
        drawer.renderHTML(html);
        drawer.open(opener);
        const cart = await fetchCart();
        Track.addToCart(cart, payload.items.map((i) => i.id), addedNow);
        refreshCartCount(cart);
        CartBus.publish(cart);
        return cart;
      }

      const cart = await fetchCart();
      Track.addToCart(cart, payload.items.map((i) => i.id), addedNow);
      await renderCartSections(cart);
      if (useDrawer) drawer.open(opener);
      else window.location.href = T.routes.cart;
      return cart;
    },

    /* Quantity changes go through CartLines: one queue, lines found by variant. */
    change(variantId, quantity) {
      CartLines.set(variantId, quantity);
      return CartLines.settle();
    }
  };

  /* ------------------------------------------------------------ cart lines
     One queue for every quantity change in the cart drawer and on /cart.
     - A line is found by its variant, never by its position: positions shift when a
       line is removed, and a second tap on Remove used to delete the next line.
     - The number the shopper asked for is kept here until the server confirms it, so a
       redraw between two fast taps can no longer put an older number back.
     - Requests go out one at a time, after a short pause in the tapping, and the cart
       is redrawn once at the end, from the HTML that came back with the last change. */
  const CartLines = (() => {
    const wanted = new Map(); /* variant id -> { qty, key } */
    let timer = null;
    let running = null;
    let shiftedAt = 0;
    let failures = 0;

    const roots = () => document.querySelectorAll('cart-drawer, [data-cart-page-section]');
    const setBusy = (on) => roots().forEach((root) => {
      root.classList.toggle('is-busy', on);
      if (on) root.setAttribute('aria-busy', 'true');
      else root.removeAttribute('aria-busy');
    });

    /* Show the asked-for numbers (again) in the boxes; a line waiting to be removed is dimmed. */
    function paint() {
      wanted.forEach((want, id) => {
        document.querySelectorAll(`[data-line-qty][data-variant="${id}"]`).forEach((box) => {
          if (want.qty > 0) box.value = want.qty;
          const item = box.closest('[data-line-item]');
          if (item) item.classList.toggle('is-removing', want.qty === 0);
        });
      });
    }

    /* Back to the numbers the server last rendered (a failed change must not leave a line
       dimmed and untappable, or a number the cart does not hold). */
    function unpaint() {
      roots().forEach((root) => root.querySelectorAll('[data-line-item]').forEach((item) => {
        item.classList.remove('is-removing');
        const box = item.querySelector('[data-line-qty]');
        if (box) box.value = box.defaultValue;
      }));
    }

    async function flush() {
      let cart = await fetchCart();
      let html = null;
      let failed = null;
      let removed = false;
      while (wanted.size) {
        const [id, want] = wanted.entries().next().value;
        const lines = cart.items.filter((item) => String(item.variant_id) === id);
        const line = lines.find((item) => item.key === want.key) || lines[0];
        /* Nothing to do: the line is already gone (second tap on Remove) or already at that number. */
        if (!line || line.quantity === want.qty) { wanted.delete(id); continue; }
        try {
          /* The key comes from the newest cart: it changes when a pack deal starts or stops applying. */
          cart = await cartRequest(T.routes.cartChange, { id: line.key, quantity: want.qty, sections: cartSectionIds() });
          html = cart.sections || null;
          if (want.qty === 0) removed = true;
        } catch (error) {
          failed = error;
          failures += 1;
          html = null;
          wanted.delete(id);
          /* Read the cart again: after a timeout the store may still have applied the change. */
          cart = await fetchCart();
          continue;
        }
        /* A newer tap on the same line replaced the entry: it stays and goes out next. */
        if (wanted.get(id) === want) wanted.delete(id);
      }
      await renderCartSections(cart, html);
      if (removed) shiftedAt = performance.now();
      if (failed) toast(failed.message, 'error');
    }

    function run() {
      window.clearTimeout(timer);
      timer = null;
      if (!running) {
        running = flush()
          .catch((error) => {
            failures += 1;
            wanted.clear();
            unpaint();
            toast((error && error.message) || T.strings.cartError, 'error');
            return fetchCart().then((cart) => renderCartSections(cart)).catch(() => {});
          })
          .finally(() => {
            running = null;
            if (wanted.size) schedule();
            else setBusy(false);
          });
      }
      return running;
    }

    function schedule() {
      window.clearTimeout(timer);
      timer = window.setTimeout(run, 320);
    }

    return {
      /* Ask for `qty` of a variant's line (0 removes it). */
      set(variantId, qty, key) {
        const id = String(variantId || '');
        if (!id || !Number.isFinite(qty) || qty < 0) return;
        wanted.set(id, { qty, key: key || '' });
        setBusy(true);
        paint();
        schedule();
      },
      /* Resolves once nothing is waiting or in flight; sends what is waiting straight away. */
      settle() {
        if (timer) run();
        return running ? running.then(() => this.settle()) : Promise.resolve();
      },
      pending: () => !!timer || !!running,
      /* Grows by one for every change that did not go through. */
      failures: () => failures,
      /* True for half a second after a removal moved the list up under the finger. */
      justShifted: () => performance.now() - shiftedAt < 500,
      paint
    };
  })();

  /* The cart is redrawn after a change and the focused control is replaced. Remember which
     control had focus (the line's variant and the control's role) and put focus back on
     the new copy, so screen-reader and keyboard users keep their place. */
  function focusKey(root) {
    const el = document.activeElement;
    if (!root || !el || el === document.body || !root.contains(el)) return null;
    const item = el.closest('[data-line-item]');
    const input = item && item.querySelector('[data-line-qty]');
    return {
      id: el.id || '',
      variant: input ? input.dataset.variant : '',
      index: item ? Array.prototype.indexOf.call(root.querySelectorAll('[data-line-item]'), item) : -1,
      role: el.matches('[data-line-remove]') ? 'remove' : el.dataset.step ? `step-${el.dataset.step}` : el.matches('[data-line-qty]') ? 'qty' : ''
    };
  }
  function restoreFocus(root, key) {
    if (!root || !key) return;
    const items = root.querySelectorAll('[data-line-item]');
    let item = null;
    if (key.variant) {
      const input = root.querySelector(`[data-line-qty][data-variant="${key.variant}"]`);
      item = input ? input.closest('[data-line-item]') : null;
    }
    /* The line is gone (removed): the one that took its place gets the focus. */
    if (!item && key.index >= 0 && items.length) item = items[Math.min(key.index, items.length - 1)];
    let target = null;
    if (item && key.role) {
      if (key.role === 'remove') target = item.querySelector('[data-line-remove]');
      else if (key.role.startsWith('step-')) target = item.querySelector(`[data-step="${key.role.slice(5)}"]`);
      else target = item.querySelector('[data-line-qty]');
    }
    if (!target && !key.role && key.id) target = document.getElementById(key.id);
    if (!target && item && !key.role) target = item.querySelector('.line-item__title');
    if (!target || !root.contains(target)) target = root.querySelector('[data-cart-drawer-close], [data-line-qty], a[href]:not([tabindex="-1"]), button');
    if (target) target.focus({ preventScroll: true });
  }

  /* --------------------------------------------------------- <cart-drawer> */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.opener = null;
      this.addEventListener('click', this.onClick.bind(this));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-drawer-open]');
        if (!trigger || T.settings.cartType !== 'drawer') return;
        e.preventDefault();
        this.open(trigger);
      });
      /* Quantity boxes and Remove are handled by initCartLines (shared with /cart). */
    }

    onClick(event) {
      const closer = event.target.closest('[data-cart-drawer-close]');
      if (!closer && !event.target.matches('.cart-drawer__scrim')) return;
      /* On the cart page itself, "Continue shopping" in the empty drawer must leave the page. */
      if (closer && closer.matches('a[href]') && document.querySelector('[data-cart-page-section]')) return;
      event.preventDefault();
      this.close();
    }

    async rerender() {
      const data = await fetchCartSections('cart-drawer');
      if (data['cart-drawer']) this.renderHTML(data['cart-drawer']);
    }

    renderHTML(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('cart-drawer');
      if (!next) return;
      const restore = focusKey(this);
      /* The scrolling box is replaced with the rest: remember where the list was. */
      const scrolled = ['.cart-drawer__panel', '.cart-drawer__body'].map((sel) => {
        const box = this.querySelector(sel);
        return box ? box.scrollTop : 0;
      });
      this.innerHTML = next.innerHTML;
      ['.cart-drawer__panel', '.cart-drawer__body'].forEach((sel, i) => {
        const box = this.querySelector(sel);
        if (box && scrolled[i]) box.scrollTop = scrolled[i];
      });
      CartLines.paint();
      queueReconvert();
      restoreFocus(this, restore);
    }

    open(opener) {
      this.opener = opener || null;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      trapFocus(this, this.opener);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      releaseFocus(this);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* The struck-through price, on or off, together with the hidden words ("Regular price",
     "Sale price") that tell screen readers which price is which. */
  function setCompare(host, text) {
    if (!host) return;
    const compare = host.querySelector('[data-price-compare]');
    if (!compare) return;
    if (text) setMoney(compare, text);
    compare.hidden = !text;
    host.querySelectorAll('[data-price-label]').forEach((label) => { label.hidden = !text; });
  }

  /* ------------------------------------------------------- <product-form> */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.submitButton = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      // Let Shopify handle the dynamic checkout button natively.
      if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      /* The sticky bar calls requestSubmit(), which fires even while the button is disabled. */
      if (this.busy) return;
      /* "Checkout now" is working on the same pack: adding here as well would double it. */
      if (document.querySelector('[data-bundle-checkout].is-loading')) return;
      this.busy = true;
      this.loadingOpener = this.form._opener && this.form._opener !== this.submitButton ? this.form._opener : null;
      this.setLoading(true);
      if (this.errorEl) this.errorEl.hidden = true;

      const formData = new FormData(this.form);
      const items = [];

      const mainId = formData.get('id');
      const mainQty = Math.max(1, parseInt(formData.get('quantity') || '1', 10) || 1);
      if (mainId) items.push({ id: Number(mainId), quantity: mainQty });

      /* A pack with mixed colours (the rows under the pack cards): one line per colour
         instead of the form's single variant. */
      const idField = this.form.querySelector('[data-variant-id-input]');
      const mixHost = idField && document.querySelector(`[data-pack-colours="${idField.dataset.variantIdInput}"]`);
      if (mixHost && !mixHost.hidden && mixHost.dataset.mixItems) {
        try {
          const mix = JSON.parse(mixHost.dataset.mixItems);
          if (Array.isArray(mix) && mix.length) items.splice(0, items.length, ...mix);
        } catch (e) { /* keep the form's own line */ }
      }

      // Optional bundled free-gift / add-on items declared as data attributes.
      this.querySelectorAll('[data-addon-variant]:checked').forEach((el) => {
        items.push({ id: Number(el.dataset.addonVariant), quantity: Number(el.dataset.addonQty || 1) });
      });

      try {
        /* The sticky bar submits this form; it is the control focus goes back to.
           The promise is kept so "Checkout now" can wait for this add before it reads the cart. */
        this.pending = window.ThemeCart.add(items, this.form._opener || this.submitButton);
        await this.pending;
      } catch (error) {
        /* A partial add (stock limit) still changed the cart: show it. */
        fetchCart().then((cart) => renderCartSections(cart)).catch(() => {});
        if (this.errorEl) {
          this.errorEl.textContent = error.message;
          this.errorEl.hidden = false;
        }
        toast(error.message, 'error');
      } finally {
        this.pending = null;
        this.setLoading(false);
        this.busy = false;
        this.loadingOpener = null;
      }
    }

    /* "Checkout now" holds the form while it works: Add to cart and the sticky button are
       switched off (no spinner) so the same pack cannot be added a second time. */
    hold(on) {
      this.busy = on;
      if (this.submitButton) this.submitButton.disabled = on;
      const sticky = document.querySelector('[data-sticky-add]');
      if (sticky) sticky.disabled = on;
    }

    setLoading(on) {
      [this.submitButton, this.loadingOpener].forEach((btn) => {
        if (!btn) return;
        btn.classList.toggle('is-loading', on);
        btn.disabled = on;
      });
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------------------------------------------------- <variant-selects> */

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.url = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl !== 'false';

      const dataEl = this.querySelector('[data-variant-data]');
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];

      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
      /* A colour tapped before this script was ready stays ticked on the page while the form
         still holds the first colour. Once everything is wired, bring them in line. */
      window.setTimeout(() => {
        const picked = this.findVariant(this.selectedOptions);
        const idInput = document.querySelector(`[data-variant-id-input="${this.sectionId}"]`);
        if (picked && idInput && String(picked.id) !== String(idInput.value)) this.onChange();
      }, 0);
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-index]'))
        .filter((el) => (el.type === 'radio' ? el.checked : true))
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
        .map((el) => el.value);
    }

    findVariant(options) {
      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === options[index])
      );
    }

    onChange() {
      const options = this.selectedOptions;
      const variant = this.findVariant(options);

      this.querySelectorAll('[data-option-display]').forEach((el) => {
        const index = Number(el.dataset.optionDisplay);
        if (options[index] !== undefined) el.textContent = options[index];
      });

      this.updateAvailability();

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.currentVariant = variant;
      this.updateIdInput(variant);
      this.updatePrice(variant);
      this.updateMedia(variant);
      this.updateBuyButton(variant);
      this.updateStickyBar(variant);

      if (this.updateUrl && this.url) {
        const u = new URL(window.location.href);
        u.searchParams.set('variant', variant.id);
        const pack = document.querySelector('input[data-bundle-variant]:checked');
        if (pack && pack.dataset.bundleQty) u.searchParams.set('pack', pack.dataset.bundleQty);
        u.searchParams.delete('lang_hop');
        window.history.replaceState({}, '', `${u.pathname}${u.search}`);
      }

      document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant, sectionId: this.sectionId } }));
    }

    /* Grey out option values that pair with no purchasable variant. */
    updateAvailability() {
      const inputs = Array.from(this.querySelectorAll('input[data-option-index]'));
      if (!inputs.length) return;

      const current = this.selectedOptions;

      inputs.forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const candidate = current.slice();
        candidate[index] = input.value;

        const match = this.variants.filter((variant) =>
          variant.options.every((value, i) => (i === index ? value === input.value : value === candidate[i]))
        );

        const available = match.some((variant) => variant.available);
        input.disabled = match.length > 0 && !available;
      });
    }

    setUnavailable() {
      const button = document.querySelector(`[data-add-button="${this.sectionId}"]`);
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        const label = button.querySelector('.btn__label');
        if (label) label.textContent = T.strings.unavailable;
      }
      const price = document.querySelector(`[data-price="${this.sectionId}"]`);
      if (price) price.style.opacity = '0.4';
    }

    updateIdInput(variant) {
      document.querySelectorAll(`[data-variant-id-input="${this.sectionId}"]`).forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updatePrice(variant) {
      const host = document.querySelector(`[data-price="${this.sectionId}"]`);
      if (!host) return;
      host.style.opacity = '1';

      const regular = host.querySelector('[data-price-regular]');
      if (regular) setMoney(regular, formatMoney(variant.price));

      const onSale = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
      setCompare(host, onSale ? formatMoney(variant.compare_at_price) : '');
      host.classList.toggle('price--on-sale', onSale);

      const saveEl = document.querySelector(`[data-price-save="${this.sectionId}"]`);
      if (saveEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          saveEl.textContent = `Save ${pct}%`;
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = document.querySelector(`product-gallery[data-section="${this.sectionId}"]`);
      if (gallery) gallery.selectByMediaId(String(variant.featured_media.id));
    }

    updateBuyButton(variant) {
      const button = document.querySelector(`[data-add-button="${this.sectionId}"]`);
      if (!button) return;

      const label = button.querySelector('.btn__label');
      const available = variant.available;

      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
      if (label) label.textContent = available ? T.strings.addToCart : T.strings.soldOut;

      const stock = document.querySelector(`[data-stock-line="${this.sectionId}"]`);
      if (stock) stock.hidden = !available;
    }

    updateStickyBar() {
      const bar = document.querySelector('sticky-atc');
      if (bar && bar.syncPrice) bar.syncPrice();
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /* ---------------------------------------------------- <product-gallery> */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb-media-id]'));
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));

      /* Once the page has loaded, prepare the photos either side of the one on screen. */
      window.addEventListener('load', () => {
        const n = this.slides.length;
        if (n > 1) window.setTimeout(() => { this.hydrate((this.index + 1) % n); this.hydrate((this.index - 1 + n) % n); }, 1500);
      }, { once: true });

      const strip = this.querySelector('.gallery__thumbs');
      const firstActive = this.thumbs[this.index];
      if (strip && firstActive && strip.scrollWidth > strip.clientWidth) {
        strip.scrollLeft = Math.max(0, firstActive.offsetLeft - (strip.clientWidth - firstActive.offsetWidth) / 2);
      }

      this.querySelectorAll('[data-gallery-nav]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.select(this.packShowing() ? this.index : this.index + (btn.dataset.galleryNav === 'next' ? 1 : -1));
        });
      });

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.select(i));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.select(this.index + 1);
        if (event.key === 'ArrowLeft') this.select(this.index - 1);
      });

      /* Swipe on the main photo, the way people browse photos on a phone. */
      const main = this.querySelector('.gallery__main');
      if (main && this.slides.length > 1) {
        let x0 = null, y0 = null;
        main.addEventListener('touchstart', (event) => {
          const t = event.touches[0];
          x0 = t.clientX; y0 = t.clientY;
        }, { passive: true });
        main.addEventListener('touchend', (event) => {
          if (x0 === null) return;
          const t = event.changedTouches[0];
          const dx = t.clientX - x0, dy = t.clientY - y0;
          x0 = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) this.select(this.packShowing() ? this.index : this.index + (dx < 0 ? 1 : -1));
        }, { passive: true });
      }
    }

    hydrate(i) {
      const slide = this.slides[i];
      const img = slide && slide.querySelector('img[data-srcset]');
      if (!img) return;
      img.srcset = img.dataset.srcset;
      img.removeAttribute('data-srcset');
      img.loading = 'eager';
    }

    packShowing() {
      const preview = this.querySelector('[data-bundle-preview]');
      return !!(preview && preview.classList.contains('is-visible'));
    }

    select(next) {
      if (!this.slides.length) return;
      const count = this.slides.length;
      this.index = ((next % count) + count) % count;
      this.hydrate(this.index);
      this.hydrate((this.index + 1) % count);

      /* The pack photo sits above the slides. Any move through the gallery
         (thumbnail, arrow, swipe, colour change) shows the slide instead. */
      const preview = this.querySelector('[data-bundle-preview]');
      if (preview) preview.classList.remove('is-visible');

      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === this.index);
        const media = slide.querySelector('video');
        if (media && i !== this.index) media.pause();
      });

      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.index);
        thumb.setAttribute('aria-selected', String(i === this.index));
      });

      const activeThumb = this.thumbs[this.index];
      /* Scroll only the strip, sideways. scrollIntoView also scrolled the page,
         so a colour tap threw the shopper ~600 px up, away from the buttons. */
      const strip = this.querySelector('.gallery__thumbs');
      if (activeThumb && strip && strip.scrollWidth > strip.clientWidth) {
        const left = activeThumb.offsetLeft - (strip.clientWidth - activeThumb.offsetWidth) / 2;
        strip.scrollTo({ left: Math.max(0, left), behavior: RM.matches ? 'auto' : 'smooth' });
      }
    }

    selectByMediaId(id) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === id);
      if (index > -1) this.select(index);
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* --------------------------------------------------------- <sticky-atc> */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.anchor = document.querySelector(this.dataset.anchor || '[data-atc-anchor]');
      this.button = this.querySelector('[data-sticky-add]');

      if (this.button) {
        this.button.addEventListener('click', () => {
          const form = document.querySelector('product-form form');
          const host = form && form.closest('product-form');
          if (form && !(host && host.busy)) {
            form._opener = this.button;
            if (form.requestSubmit) form.requestSubmit();
            else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            window.setTimeout(() => { form._opener = null; }, 4000);
          }
        });
      }

      /* The bar shows what the button will add: the quantity in the box and
         the price on the page (pack price included), kept in step with both. */
      const priceEl = this.querySelector('[data-sticky-price]');
      const qtyEl = this.querySelector('[data-sticky-qty]');
      const mainPrice = document.querySelector('[data-price] [data-price-regular]');
      const qtyBox = document.querySelector('[data-quantity-input]');
      this.syncPrice = () => {
        if (priceEl && mainPrice) {
          /* Copy the store-currency text, never the visible one: once the converter app
             has redrawn the main price, copying it would convert the amount twice. */
          const base = mainPrice.dataset.moneyBase || mainPrice.textContent.trim();
          if (priceEl.dataset.moneyBase !== base) setMoney(priceEl, base);
        }
        if (qtyEl && qtyBox) {
          const n = Math.max(1, parseInt(qtyBox.value, 10) || 1);
          qtyEl.textContent = `${this.dataset.qtyLabel || ''} ${n} · `.trimStart();
        }
      };
      if (mainPrice && 'MutationObserver' in window) {
        this.priceObserver = new MutationObserver(this.syncPrice);
        this.priceObserver.observe(mainPrice, { childList: true, characterData: true, subtree: true });
      }
      if (qtyBox) {
        qtyBox.addEventListener('change', this.syncPrice);
        qtyBox.addEventListener('input', this.syncPrice);
      }
      this.syncPrice();

      if (!this.anchor) return;

      this.hasPulsed = false;

      /* Position check on scroll. An IntersectionObserver on the top edge of
         the screen misses a fast flick that carries the buttons past that edge
         between two frames, which left the bar stuck hidden or stuck open. */
      let queued = false;
      const update = () => {
        queued = false;
        const header = document.querySelector('.header-section');
        const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const show = this.anchor.getBoundingClientRect().bottom < headerBottom;
        if (show === this.classList.contains('is-visible')) return;
        this.classList.toggle('is-visible', show);
        this.inert = !show;
        this.setAttribute('aria-hidden', String(!show));
        document.documentElement.style.setProperty('--sticky-bar-height', show ? `${this.offsetHeight}px` : '0px');

        // One pulse the first time it appears: a nudge, not a carnival.
        if (show && !this.hasPulsed && !RM.matches) {
          this.hasPulsed = true;
          this.classList.add('is-pulsing');
          window.setTimeout(() => this.classList.remove('is-pulsing'), 700);
        }
      };
      this.onScroll = () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(update);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      update();
    }

    disconnectedCallback() {
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.priceObserver) this.priceObserver.disconnect();
      document.documentElement.style.setProperty('--sticky-bar-height', '0px');
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ------------------------------------------------------- <quantity-input> */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      /* Letters, blanks and numbers below the minimum become the minimum when the
         shopper leaves the box (cart lines keep 0 = remove). */
      const normalise = () => {
        const min = Number(this.input.min || 1);
        const v = parseInt(this.input.value, 10);
        if (Number.isFinite(v) && v >= min) return;
        /* Cart lines: an empty or invalid box means "no change", never "remove". */
        this.input.value = this.input.hasAttribute('data-line-qty') ? this.input.defaultValue : min;
      };
      this.input.addEventListener('blur', () => {
        const before = this.input.value;
        normalise();
        if (this.input.value !== before) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = button.dataset.step === 'up' ? 1 : -1;
          const min = Number(this.input.min || 1);
          /* A stock number of 0 or less (overselling allowed, tracking off) is not a limit. */
          const limit = Number(this.input.max);
          const max = Math.min(limit > 0 ? limit : Infinity, MAX_PER_VARIANT);
          let current = parseInt(this.input.value, 10);
          if (!Number.isFinite(current) && this.input.hasAttribute('data-line-qty')) current = parseInt(this.input.defaultValue, 10);
          const next = Math.min(max, Math.max(min, (Number.isFinite(current) ? current : min) + step));

          if (next === Number(this.input.value)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------ <mobile-nav> */

  class MobileNav extends HTMLElement {
    connectedCallback() {
      this.opener = null;

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-mobile-nav-open]');
        if (trigger) {
          event.preventDefault();
          this.open(trigger);
        }
      });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-mobile-nav-close]') || event.target.matches('.mobile-nav__scrim')) {
          event.preventDefault();
          this.close();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      // Collapsible sub-menus
      this.querySelectorAll('[data-submenu-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          const panel = button.nextElementSibling;
          if (panel) panel.hidden = expanded;
        });
      });
    }

    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (opener) opener.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      trapFocus(this, opener);
    }

    close() {
      if (this.opener) this.opener.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      releaseFocus(this);
    }
  }
  customElements.define('mobile-nav', MobileNav);

  /* --------------------------------------------------------- <accordion-list> */

  class AccordionList extends HTMLElement {
    connectedCallback() {
      this.single = this.dataset.single === 'true';
      this.triggers = Array.from(this.querySelectorAll('.accordion__trigger'));

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const expanded = trigger.getAttribute('aria-expanded') === 'true';

          if (this.single && !expanded) {
            this.triggers.forEach((other) => this.setState(other, false));
          }
          this.setState(trigger, !expanded);
        });
      });
    }

    setState(trigger, open) {
      trigger.setAttribute('aria-expanded', String(open));
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (panel) panel.dataset.open = String(open);
    }
  }
  customElements.define('accordion-list', AccordionList);

  /* ----------------------------------------------------- <announcement-bar> */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.announcement__slide'));
      if (this.slides.length < 2 || RM.matches) return;

      const interval = Number(this.dataset.interval || 5) * 1000;
      let index = 0;

      let paused = false;
      const pause = () => { paused = true; };
      const resume = () => { paused = false; };
      ['pointerdown', 'mouseenter', 'focusin'].forEach((e) => this.addEventListener(e, pause));
      ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, resume));
      this.addEventListener('pointerup', () => window.setTimeout(resume, 4000));
      this.timer = window.setInterval(() => {
        if (paused) return;
        this.slides[index].classList.remove('is-active');
        index = (index + 1) % this.slides.length;
        this.slides[index].classList.add('is-active');
      }, interval);
    }

    disconnectedCallback() {
      if (this.timer) window.clearInterval(this.timer);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* -------------------------------------------------------- <marquee-strip> */

  class MarqueeStrip extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('.marquee__track');
      if (!track || RM.matches) return;

      // Duplicate content so the -50% translate loops seamlessly. The copies are hidden from
      // screen readers (every claim was read twice) and from phones, where the strip stands still.
      Array.from(track.children).forEach((item) => {
        const copy = item.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.classList.add('marquee__item--copy');
        track.appendChild(copy);
      });

      const speed = Number(this.dataset.speed || 60); // px per second
      const phone = window.matchMedia('(max-width: 749px)');
      const setDuration = () => {
        if (phone.matches) return; /* the strip stands still on phones */
        this.style.setProperty('--marquee-duration', `${Math.max(12, (track.scrollWidth / 2) / speed)}s`);
      };
      setDuration();
      let resizeTimer = null;
      window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(setDuration, 200);
      }, { passive: true });
    }
  }
  customElements.define('marquee-strip', MarqueeStrip);

  /* ------------------------------------------------------------ facet form */

  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.form.addEventListener('change', () => {
        const params = new URLSearchParams(new FormData(this.form)).toString();
        window.location.search = params;
      });
    }
  }
  customElements.define('facet-form', FacetForm);

  /* ------------------------------------------------------------- behaviors */

  function initHeaderStuck() {
    const header = document.querySelector('.header');
    if (!header) return;

    const setHeight = () => {
      document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    };
    setHeight();
    window.addEventListener('resize', setHeight, { passive: true });

    const onScroll = () => header.classList.toggle('is-stuck', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initReveal() {
    if (!document.body.classList.contains('anim-on') || RM.matches) {
      document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    /* Everything already on screen is marked revealed before the hidden state is
       switched on, so the first paint never waits for this script. */
    const vh = window.innerHeight || document.documentElement.clientHeight;
    document.querySelectorAll('[data-reveal]').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < vh * 1.1 && r.bottom > 0) el.classList.add('is-revealed');
    });
    document.body.classList.add('anim-ready');

    document.querySelectorAll('[data-reveal]').forEach((el, i) => {
      // Stagger siblings within the same parent for a gentle cascade.
      const siblings = Array.from(el.parentElement ? el.parentElement.children : []).filter((n) =>
        n.hasAttribute('data-reveal')
      );
      const position = siblings.indexOf(el);
      el.style.setProperty('--reveal-delay', `${Math.min(position, 5) * 70}ms`);
      observer.observe(el);
    });
  }

  function initExternalLinks() {
    document.querySelectorAll('a[target="_blank"]').forEach((link) => {
      if (link.getAttribute('aria-describedby')) return;
      link.setAttribute('aria-describedby', 'a11y-new-window-message');
      link.setAttribute('rel', (link.getAttribute('rel') || '').includes('noopener') ? link.getAttribute('rel') : 'noopener noreferrer');
    });
  }

  /* Homepage pack cards: the shopper picks a colour, then a card's button adds that pack
     in that colour and opens the checkout. The buttons carry the pack size; the colour
     picker hands them the variant. */
  function initOfferCheckout() {
    document.querySelectorAll('[data-offer]').forEach((root) => {
      /* Safe to call again (the theme editor reloads sections). */
      if (root.dataset.offerReady === '1') return;
      root.dataset.offerReady = '1';
      const buttons = Array.from(root.querySelectorAll('[data-offer-checkout]'));
      if (!buttons.length) return;
      const nameEl = root.querySelector('[data-offer-colour-name]');
      const mix = root.querySelector('[data-offer-mix]');
      let lockedColour = '';

      const sync = () => {
        const picked = root.querySelector('input[data-offer-colour]:checked');
        if (!picked) return;
        buttons.forEach((button) => { button.dataset.offerVariant = picked.value; });
        if (nameEl) nameEl.textContent = picked.dataset.offerColour;
        root.querySelectorAll('[data-offer-thumb]').forEach((img) => { img.hidden = img.dataset.offerThumb !== picked.value; });
        if (mix) {
          try {
            const u = new URL(mix.getAttribute('href'), window.location.origin);
            u.searchParams.set('variant', picked.value);
            mix.setAttribute('href', `${u.pathname}${u.search}`);
          } catch (e) { /* keep the plain link */ }
        }
      };
      const release = () => {
        root.dataset.busy = '';
        lockedColour = '';
        buttons.forEach((button) => {
          button.disabled = false;
          button.classList.remove('is-loading');
        });
      };
      root.addEventListener('change', (event) => {
        if (!event.target.matches('input[data-offer-colour]')) return;
        /* While a Buy now is on its way, the colour stays the one that is being bought. */
        if (root.dataset.busy === '1' && lockedColour) {
          const keep = root.querySelector(`input[data-offer-colour][value="${lockedColour}"]`);
          if (keep) keep.checked = true;
        }
        sync();
      });
      /* ?variant= in the address (language switch, a link): start on that colour. */
      try {
        const asked = new URLSearchParams(window.location.search).get('variant');
        const match = asked && Array.from(root.querySelectorAll('input[data-offer-colour]')).find((radio) => radio.value === asked && !radio.disabled);
        if (match) match.checked = true;
      } catch (e) { /* keep the default colour */ }
      sync();

      const buy = async (button) => {
        if (root.dataset.busy === '1') return;
        root.dataset.busy = '1';
        /* Read once: the colour and the pack that were on screen at the tap are what is bought. */
        const variantId = button.dataset.offerVariant;
        const quantity = Number(button.dataset.offerQty || 1);
        lockedColour = variantId;
        button.classList.add('is-loading');
        buttons.forEach((other) => { other.disabled = true; });
        try {
          const result = await setVariantQuantity(variantId, quantity);
          Track.addToCart(result, [variantId], result.added);
          const left = await checkoutOrShowCart(result, button.dataset.offerCheckout || '/checkout', button);
          if (!left) release();
        } catch (e) {
          release();
          /* The button was switched off while it worked and lost the focus. */
          if (document.activeElement === document.body) button.focus({ preventScroll: true });
          toast((e && e.message) || T.strings.cartError, 'error');
          /* A stock limit may still have added part of the quantity: show the real cart. */
          fetchCart().then((cart) => renderCartSections(cart)).catch(() => {});
        }
      };
      /* The buttons sit in a plain add-to-cart form, so they still add the pack (and land on
         the cart page) if this script never loads. Here the submit is taken over: exact
         quantity, tracking, then straight to the checkout. */
      const form = root.querySelector('[data-offer-form]');
      if (form) {
        form.addEventListener('submit', (event) => {
          const button = event.submitter;
          /* No submitter (old Safari): the plain form post adds the pack and shows the cart page. */
          if (!button || !button.matches('[data-offer-checkout]')) return;
          event.preventDefault();
          buy(button);
        });
        /* Enter on a colour must not submit the form through its first button. */
        form.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' && event.target.matches('input[type="radio"]')) event.preventDefault();
        });
      } else {
        buttons.forEach((button) => button.addEventListener('click', () => buy(button)));
      }

      /* Back from checkout (the page comes back as it was left): the buttons work again. */
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted || !root.isConnected) return;
        release();
        sync();
      });
    });
  }

  function initBundleSelector() {
    document.querySelectorAll('[data-bundle-selector]').forEach((group) => {
      const sectionId = group.dataset.bundleSelector;
      const checkoutBtn = document.querySelector(`[data-bundle-checkout="${sectionId}"]`);
      let askedAtLoad = NaN;
      try { askedAtLoad = parseInt(new URLSearchParams(window.location.search).get('pack'), 10); } catch (e) { /* keep NaN */ }
      /* A link asking for more than can be bought is ignored (it showed absurd prices). */
      if (!(askedAtLoad >= 1 && askedAtLoad <= MAX_PER_VARIANT)) askedAtLoad = NaN;
      const askedNoCard = askedAtLoad > 0 && !group.querySelector(`input[data-bundle-variant][data-bundle-qty="${askedAtLoad}"]`);

      function rememberPack(n) {
        try {
          const u = new URL(window.location.href);
          if (n && n > 0) u.searchParams.set('pack', n); else u.searchParams.delete('pack');
          u.searchParams.delete('lang_hop');
          window.history.replaceState(window.history.state, '', `${u.pathname}${u.search}${u.hash}`);
        } catch (e) { /* old browsers: the address simply does not change */ }
      }

      /* The total for a quantity that has no pack card (4, 5, 7...), shown right above the
         buttons: the price line under the title is off screen there. n = 0 hides it. */
      const totalLine = group.querySelector('[data-pack-total]');
      function showTotalLine(n, total, before) {
        if (!totalLine) return;
        if (!n) { totalLine.hidden = true; return; }
        totalLine.textContent = '';
        const text = (totalLine.dataset.template || '%%count%%: %%price%%').replace('%%count%%', n).split('%%price%%');
        totalLine.append(text[0]);
        const strong = document.createElement('strong');
        strong.innerHTML = moneyHtml(total);
        totalLine.append(strong);
        if (before) {
          const old = document.createElement('s');
          old.innerHTML = moneyHtml(before);
          totalLine.append(' ', old);
        }
        if (text[1]) totalLine.append(text[1]);
        totalLine.hidden = false;
        queueReconvert();
      }

      function apply(input, showPreview = true, remember = true) {
        if (remember) rememberPack(parseInt(input.dataset.bundleQty || '1', 10));
        const idInput = document.querySelector(`[data-variant-id-input="${sectionId}"]`);
        const qtyInput = document.querySelector(`[data-quantity-input="${sectionId}"]`);
        const qty = parseInt(input.dataset.bundleQty || '1', 10);

        if (idInput) idInput.value = input.dataset.bundleVariant;
        /* Leave the box alone while the shopper is typing the same number ("1." on the way to "1.5"). */
        if (qtyInput && parseInt(qtyInput.value, 10) !== qty) qtyInput.value = qty;
        showTotalLine(0);

        const price = document.querySelector(`[data-price="${sectionId}"] [data-price-regular]`);
        if (price && input.dataset.bundlePrice) setMoney(price, input.dataset.bundlePrice);

        setCompare(document.querySelector(`[data-price="${sectionId}"]`), input.dataset.bundleCompare || '');

        /* Pack photo cross-fades in over the gallery. */
        const preview = document.querySelector(`[data-bundle-preview="${sectionId}"]`);
        if (preview && showPreview) {
          if (input.dataset.bundleImage) {
            if (preview.getAttribute('src') !== input.dataset.bundleImage) {
              preview.srcset = input.dataset.bundleSrcset || '';
              preview.src = input.dataset.bundleImage;
            }
            preview.classList.add('is-visible');
          } else {
            preview.classList.remove('is-visible');
          }
        }

        /* "Checkout now" shows for every pack: with the PayPal express button
           gone it is the quick way to the payment step. */
        if (checkoutBtn) checkoutBtn.hidden = group.dataset.available === 'false';
        colourRows.sync(qty);
      }

      /* Colour of each item in a pack: one row per item under the pack cards, so a 3-pack can
         be 2 Blue + 1 Pink. The rows follow the quantity box, the main colour swatch resets
         every row, and a mixed choice is kept on the block (data-mix-items) for Add to cart,
         the sticky button and "Checkout now". Rows stop at 6 items; above that the main colour
         applies to all. */
      const colourRows = (() => {
        const off = { sync() {}, reset() {}, items() { return null; } };
        const host = document.querySelector(`[data-pack-colours="${sectionId}"]`);
        const picker = document.querySelector(`variant-selects[data-section="${sectionId}"]`);
        if (!host || !picker || typeof picker.findVariant !== 'function') return off;
        const rowsEl = host.querySelector('[data-pack-colour-rows]');
        const summaryEl = host.querySelector('[data-pack-colour-summary]');
        const swatches = Array.from(picker.querySelectorAll('.swatch--color input[data-option-index]'));
        if (!rowsEl || swatches.length < 2) return off;
        const MAX_ROWS = 6;
        const optionIndex = Number(swatches[0].dataset.optionIndex);
        const colours = swatches.map((input) => {
          const chip = input.nextElementSibling && input.nextElementSibling.querySelector('.swatch__chip');
          return { value: input.value, style: chip ? chip.getAttribute('style') || '' : '' };
        });
        let choices = [];

        const mainColour = () => {
          const checked = swatches.find((i) => i.checked);
          return checked ? checked.value : colours[0].value;
        };
        const variantFor = (colour) => {
          const options = picker.selectedOptions.slice();
          options[optionIndex] = colour;
          return picker.findVariant(options);
        };
        const inStock = (colour) => { const v = variantFor(colour); return !!v && v.available !== false; };

        /* Counts per colour -> summary line and the lines to add. */
        const publish = () => {
          const parts = [];
          const items = [];
          colours.forEach((c) => {
            const n = choices.filter((x) => x === c.value).length;
            if (!n) return;
            parts.push(`${n} × ${c.value}`);
            const v = variantFor(c.value);
            if (v) items.push({ id: Number(v.id), quantity: n });
          });
          const mixed = parts.length > 1;
          if (mixed && items.length === parts.length) host.dataset.mixItems = JSON.stringify(items);
          else delete host.dataset.mixItems;
          host.dataset.mix = parts.join(' + ');
          if (!summaryEl) return;
          summaryEl.textContent = '';
          if (mixed) {
            const text = (host.dataset.summary || '%%mix%%').split('%%mix%%');
            summaryEl.append(text[0]);
            const strong = document.createElement('strong');
            strong.textContent = parts.join(' + ');
            summaryEl.append(strong);
            if (text[1]) summaryEl.append(text[1]);
          }
          summaryEl.hidden = !mixed;
        };

        const render = (n) => {
          rowsEl.textContent = '';
          const label = host.dataset.rowLabel || '%%n%%';
          for (let r = 0; r < n; r += 1) {
            const row = document.createElement('div');
            row.className = 'pack-colours__row';
            const name = document.createElement('span');
            name.className = 'pack-colours__label';
            name.id = `PackColour-${sectionId}-${r + 1}`;
            name.textContent = label.replace('%%n%%', r + 1);
            const chips = document.createElement('div');
            chips.className = 'pack-colours__chips';
            chips.setAttribute('role', 'radiogroup');
            chips.setAttribute('aria-labelledby', name.id);
            colours.forEach((c, ci) => {
              const wrap = document.createElement('div');
              wrap.className = 'pack-colours__chip';
              const input = document.createElement('input');
              input.type = 'radio';
              input.name = `pack-colour-${sectionId}-${r + 1}`;
              input.id = `${input.name}-${ci + 1}`;
              input.value = c.value;
              input.autocomplete = 'off';
              input.dataset.packColourRow = String(r + 1);
              input.checked = choices[r] === c.value;
              input.disabled = !inStock(c.value);
              const lab = document.createElement('label');
              lab.htmlFor = input.id;
              const dot = document.createElement('span');
              dot.className = 'swatch__chip';
              dot.setAttribute('aria-hidden', 'true');
              if (c.style) dot.setAttribute('style', c.style);
              lab.append(dot, document.createTextNode(c.value));
              wrap.append(input, lab);
              chips.append(wrap);
            });
            row.append(name, chips);
            rowsEl.append(row);
          }
        };

        const sync = (n) => {
          n = parseInt(n, 10) || 0;
          const show = n >= 2 && n <= MAX_ROWS;
          const main = mainColour();
          if (choices.length > n) choices.length = n;
          while (choices.length < n) choices.push(main);
          /* A colour that sold out since it was picked goes back to the main colour. */
          choices = choices.map((c) => (inStock(c) ? c : main));
          host.hidden = !show;
          if (show) render(n); else rowsEl.textContent = '';
          publish();
        };
        const reset = () => { const main = mainColour(); choices = choices.map(() => main); };

        rowsEl.addEventListener('change', (event) => {
          const input = event.target.closest('input[data-pack-colour-row]');
          if (!input) return;
          choices[Number(input.dataset.packColourRow) - 1] = input.value;
          publish();
        });

        return {
          sync,
          reset,
          /* [{ id, quantity }] while the rows on screen hold more than one colour, else null. */
          items() {
            if (host.hidden || !host.dataset.mixItems) return null;
            try { const list = JSON.parse(host.dataset.mixItems); return Array.isArray(list) && list.length ? list : null; } catch (e) { return null; }
          }
        };
      })();

      group.addEventListener('change', (event) => {
        const input = event.target.closest('input[data-bundle-variant]');
        if (input) apply(input);
      });

      /* ?pack=2 (home page cards, ads) ticks that pack on arrival instead of
         the default one, so the page matches the card the shopper tapped. */
      try {
        const wanted = new URLSearchParams(window.location.search).get('pack');
        const match = wanted && group.querySelector(`input[data-bundle-variant][data-bundle-qty="${parseInt(wanted, 10)}"]`);
        if (match) match.checked = true;
      } catch (e) { /* old browsers: keep the default pack */ }

      const checked = group.querySelector('input[data-bundle-variant]:checked');
      if (checked) apply(checked, !askedNoCard, !askedNoCard);

      /* The pack cards are rendered with the variant of the page load. When the
         customer changes colour, carry the new variant into every card and
         re-apply the chosen pack, so "Checkout now" and "Add to cart" use the
         colour on screen and the price stays the pack price. */
      document.addEventListener('variant:changed', (event) => {
        const detail = event.detail || {};
        if (!detail.variant || String(detail.sectionId) !== String(sectionId)) return;
        group.querySelectorAll('input[data-bundle-variant]').forEach((i) => {
          i.dataset.bundleVariant = String(detail.variant.id);
        });
        /* A sold-out colour: no "Checkout now" (the Add to cart button already says Sold out). */
        group.dataset.available = String(detail.variant.available !== false);
        if (checkoutBtn) checkoutBtn.hidden = group.dataset.available === 'false';
        const current = group.querySelector('input[data-bundle-variant]:checked');
        /* The colour swatch sets every row of the pack to that colour. */
        colourRows.reset();
        /* The gallery has just moved to the colour's own photo: leave it on
           screen rather than covering it with the pack photo again. */
        const box = document.querySelector(`[data-quantity-input="${sectionId}"]`);
        const typed = box ? parseInt(box.value, 10) : NaN;
        if (current) apply(current, false);
        keepTyped(box, typed, current);
      });

      /* A quantity typed by hand (4, 7...) survives a colour change or a page
         restored from the back/forward cache instead of snapping to the pack. */
      function keepTyped(box, typed, current) {
        if (!box || !typed || typed < 1) return;
        if (current && typed === parseInt(current.dataset.bundleQty, 10)) return;
        box.value = typed;
        box.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* The quantity box is the single source of truth for both buttons. The
         pack cards set it; typing or stepping to a quantity that matches a pack
         ticks that pack, so the card, the price and the box always agree. */
      const qtyBox = document.querySelector(`[data-quantity-input="${sectionId}"]`);
      /* Price for any quantity, built from the pack cards the same way the
         automatic "deal N+" discounts are built: full 3-packs, then a 2-pack or
         a single for the rest. So 4 = 3-pack + 1, 5 = 3-pack + 2-pack. */
      const tierPrice = (q) => {
        const el = group.querySelector(`input[data-bundle-variant][data-bundle-qty="${q}"]`);
        return el && el.dataset.bundlePrice ? { str: el.dataset.bundlePrice, num: Number(el.dataset.bundlePrice.replace(/[^0-9.]/g, '')) } : null;
      };
      const showLadderPrice = (n) => {
        const p1 = tierPrice(1), p2 = tierPrice(2), p3 = tierPrice(3);
        const priceEl = document.querySelector(`[data-price="${sectionId}"] [data-price-regular]`);
        if (!p1 || !p2 || !p3 || !priceEl) return;
        /* Same as the automatic discounts: the saving grows with full packs up to
           "deal 12+" (13 has the same saving as 12) and stays fixed above that. */
        const ladder = (k) => Math.floor(k / 3) * p3.num + [0, p1.num, p2.num][k % 3];
        const capped = Math.min(n, 13);
        const total = n * p1.num - (capped * p1.num - ladder(capped));
        const fmt = (v) => p1.str.replace(/[0-9][0-9,.]*/, v.toLocaleString('en-US'));
        setMoney(priceEl, fmt(total));
        setCompare(document.querySelector(`[data-price="${sectionId}"]`), total < n * p1.num ? fmt(n * p1.num) : '');
        showTotalLine(n, fmt(total), total < n * p1.num ? fmt(n * p1.num) : '');
      };
      if (qtyBox) {
        const syncTier = () => {
          const n = Math.min(parseInt(qtyBox.value, 10), MAX_PER_VARIANT);
          if (!n || n < 1) return;
          const match = group.querySelector(`input[data-bundle-variant][data-bundle-qty="${n}"]`);
          if (match) {
            /* Always re-apply: after 4 -> 3 the 3-pack radio could still be ticked
               while the price line showed the 4-brush total. */
            match.checked = true;
            apply(match, false);
          } else {
            group.querySelectorAll('input[data-bundle-variant]').forEach((i) => { i.checked = false; });
            showLadderPrice(n);
            colourRows.sync(n);
            rememberPack(n);
            const pv = document.querySelector(`[data-bundle-preview="${sectionId}"]`);
            if (pv) pv.classList.remove('is-visible');
          }
        };
        qtyBox.addEventListener('change', () => {
          const n = parseInt(qtyBox.value, 10);
          /* The box shows the number that is used ("007" and "1.5" were kept as typed). */
          if (n >= 1) qtyBox.value = Math.min(n, MAX_PER_VARIANT);
          if (!n || n < 1) {
            qtyBox.value = 1;
            const one = group.querySelector('input[data-bundle-variant][data-bundle-qty="1"]');
            if (one) { one.checked = true; apply(one); }
          }
          syncTier();
        });
        qtyBox.addEventListener('input', syncTier);

        /* ?pack=4 (or any number without a card) on arrival: put it in the box. */
        if (askedNoCard) {
          qtyBox.value = askedAtLoad;
          qtyBox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      /* A page restored from the back/forward cache keeps its old state without
         re-running scripts: re-apply the chosen pack so the quantity and the
         buttons match the card that is ticked. */
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        const current = group.querySelector('input[data-bundle-variant]:checked');
        const box = document.querySelector(`[data-quantity-input="${sectionId}"]`);
        const typed = box ? parseInt(box.value, 10) : NaN;
        if (current) apply(current, false);
        keepTyped(box, typed, current);
      });

      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', async () => {
          const input = group.querySelector('input[data-bundle-variant]:checked') || group.querySelector('input[data-bundle-variant]');
          const idField = document.querySelector(`[data-variant-id-input="${sectionId}"]`);
          const variantId = (idField && idField.value) || (input && input.dataset.bundleVariant);
          if (!variantId) return;

          checkoutBtn.classList.add('is-loading');
          checkoutBtn.disabled = true;
          /* An Add to cart still on its way finishes first (this button sets an exact
             quantity, so the cart ends at the number in the box, not at twice that). */
          const productForm = document.querySelector('product-form');
          if (productForm && productForm.pending) {
            try { await productForm.pending; } catch (e) { /* its own handler shows the error */ }
          }
          if (productForm && productForm.hold) productForm.hold(true);
          const letGo = () => { if (productForm && productForm.hold) productForm.hold(false); };

          try {
            /* Absolute quantity from the quantity box (the pack cards set it):
               picking a 3-pack twice stays three, and "Checkout now" always
               buys what "Add to cart" would. */
            const box = document.querySelector(`[data-quantity-input="${sectionId}"]`);
            const qtyNow = box ? Math.max(1, parseInt(box.value, 10) || 1) : Number((input && input.dataset.bundleQty) || 1);
            const mix = colourRows.items();
            let result;
            if (mix) {
              /* Mixed colours: an absolute quantity per colour, one request after the other, so
                 the cart ends at exactly the rows on screen. "Other lines" are those outside the mix. */
              const mixIds = mix.map((line) => String(line.id));
              const before = await fetchCart();
              const others = before.items.some((i) => !mixIds.includes(String(i.variant_id)));
              for (const line of mix) {
                result = await setVariantQuantity(line.id, line.quantity);
                Track.addToCart(result, [line.id], result.added);
              }
              result = Object.assign({}, result, { others });
            } else {
              result = await setVariantQuantity(variantId, qtyNow);
              Track.addToCart(result, [variantId], result.added);
            }
            const left = await checkoutOrShowCart(result, `${T.routes.root.replace(/\/$/, '')}/checkout`, checkoutBtn);
            if (!left) {
              checkoutBtn.classList.remove('is-loading');
              checkoutBtn.disabled = false;
              letGo();
            }
          } catch (e) {
            checkoutBtn.classList.remove('is-loading');
            checkoutBtn.disabled = false;
            letGo();
            const err = document.querySelector('[data-form-error]');
            if (err) {
              err.textContent = (e && e.message) || window.theme.strings.cartError;
              err.hidden = false;
            }
            /* A stock limit may still have added part of the quantity: show the real cart. */
            fetchCart().then((cart) => renderCartSections(cart)).catch(() => {});
          }
        });
      }
    });
  }

  /* "Pay with MCB Juice": adds the selected pack like the pack button, tags the
     cart so the order shows the intent, then goes to checkout. The customer
     picks the "MCB Juice" manual payment method there; Shopify shows the Juice
     number and the steps only after the order is placed. */
  function initJuiceCheckout() {
    document.querySelectorAll('[data-juice-checkout]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const sectionId = btn.dataset.juiceCheckout;
        const picked = document.querySelector(`[data-bundle-selector="${sectionId}"] input[data-bundle-variant]:checked`);
        const idInput = document.querySelector(`[data-variant-id-input="${sectionId}"]`);
        const variantId = picked ? picked.dataset.bundleVariant : idInput && idInput.value;
        const qty = picked ? Number(picked.dataset.bundleQty || 1) : 1;
        if (!variantId) return;

        btn.classList.add('is-loading');
        btn.disabled = true;
        try {
          const cart = await setVariantQuantity(variantId, qty);
          await cartRequest(T.routes.cartUpdate, { attributes: { 'Payment preference': 'MCB Juice' } });
          Track.addToCart(cart, [variantId]);
          window.location.href = '/checkout';
        } catch (e) {
          btn.classList.remove('is-loading');
          btn.disabled = false;
          toast(T.strings.cartError, 'error');
        }
      });
    });
  }

  /* "Order on WhatsApp" under the buy buttons: the message follows the pack size, the colour
     (or the colour mix from the rows under the pack cards) and the typed quantity. */
  function initWhatsAppOrder() {
    document.querySelectorAll('[data-whatsapp-order]').forEach((link) => {
      const sectionId = link.dataset.whatsappOrder;
      const update = () => {
        const box = document.querySelector(`[data-quantity-input="${sectionId}"]`);
        const qty = box ? Math.max(1, parseInt(box.value, 10) || 1) : 1;
        const mixHost = document.querySelector(`[data-pack-colours="${sectionId}"]`);
        const colourEl = document.querySelector(`variant-selects[data-section="${sectionId}"] [data-option-display="0"]`);
        const colour = mixHost && !mixHost.hidden && mixHost.dataset.mixItems ? mixHost.dataset.mix : (colourEl ? colourEl.textContent.trim() : '');
        /* The translation filter HTML-escapes the title ("d&#39;un"): decode it, and drop the
           SEO tail after " | " so the message stays short. */
        const decoder = document.createElement('textarea');
        decoder.innerHTML = (link.dataset.template || '').replace(/ \| [^(]*\(/, ' (');
        const text = decoder.value.replace('%%qty%%', qty).replace('%%colour%%', colour);
        link.href = `https://wa.me/${link.dataset.number}?text=${encodeURIComponent(text)}`;
      };
      /* After the colour rows and pack cards have handled the same change. */
      const later = () => window.setTimeout(update, 0);
      document.addEventListener('change', later);
      document.addEventListener('variant:changed', later);
      update();
    });
  }

  /* body.buy-in-view while a buy control is on screen (the pack "Checkout now", the quantity
     row and Add to cart, the cart page's Checkout, the home pack cards' Buy now): theme.css
     hides the currency converter's floating button then, because on phones it sat on the
     left end of those controls (its z-index is above everything in the theme). */
  function initBuyZoneFlag() {
    const zones = Array.from(document.querySelectorAll('[data-bundle-checkout], product-form[data-atc-anchor], [data-cart-page-section] [name="checkout"], .offer-card__btn'));
    if (!zones.length) return;
    let queued = false;
    const update = () => {
      queued = false;
      const header = document.querySelector('.header-section, .header-group');
      const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const inView = zones.some((el) => {
        if (el.hidden || !el.offsetParent) return false;
        const r = el.getBoundingClientRect();
        return r.bottom > top && r.top < window.innerHeight;
      });
      document.body.classList.toggle('buy-in-view', inView);
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    /* A pack change adds or removes the colour rows and moves the buttons. */
    document.addEventListener('change', onScroll, true);
    update();
  }

  /* Back button from checkout restores the page from the back/forward cache with
     "Checkout now" still spinning and disabled, and the cart badge out of date. */
  function initBackForwardReset() {
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      document.querySelectorAll('.is-loading').forEach((el) => {
        el.classList.remove('is-loading');
        if ('disabled' in el) el.disabled = false;
        el.removeAttribute('aria-disabled');
      });
      document.querySelectorAll('product-form').forEach((host) => { if (host.hold) host.hold(false); });
      fetchCart().then((cart) => renderCartSections(cart)).catch(() => {});
      /* A drawer left open before leaving stays open in the restored page: close it. */
      const nav = document.querySelector('mobile-nav.is-open');
      if (nav && nav.close) nav.close();
      const drawer = document.querySelector('cart-drawer.is-open');
      if (drawer && drawer.close) drawer.close();
    });
  }

  /* The language switch: send the live address (with ?variant and the ticked
     pack) as return_to. Shopify renders return_to into a cached page, so it could
     carry another visitor's query string, including ?lang=fr, which bounced
     English choices straight back to French. */
  function initLanguageForm() {
    const form = document.getElementById('HeaderLanguageForm');
    if (!form) return;
    form.addEventListener('submit', (event) => {
      const field = form.querySelector('input[name="return_to"]');
      const q = new URLSearchParams(location.search);
      ['lang', 'lang_hop'].forEach((k) => q.delete(k));
      const ticked = document.querySelector('input[data-bundle-variant]:checked');
      const box = document.querySelector('[data-quantity-input]');
      const boxQty = box ? parseInt(box.value, 10) : NaN;
      if (boxQty > 0) q.set('pack', boxQty);
      else if (ticked && ticked.dataset.bundleQty) q.set('pack', ticked.dataset.bundleQty);
      const idInput = document.querySelector('[data-variant-id-input]');
      if (idInput && idInput.value) q.set('variant', idInput.value);
      /* Home page: the colour picked in the pack section. */
      const offerColour = document.querySelector('input[data-offer-colour]:checked');
      if (!idInput && offerColour) q.set('variant', offerColour.value);
      const lang = event.submitter && event.submitter.value;
      const alt = lang && document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
      if (alt) {
        event.preventDefault();
        window.location.assign(new URL(alt.href).pathname + (q.toString() ? `?${q}` : ''));
        return;
      }
      if (!field) return;
      let path = location.pathname;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      if (root !== '/' && path.startsWith(root)) path = '/' + path.slice(root.length);
      field.value = path + (q.toString() ? `?${q}` : '');
    });
  }

  /* Quantity boxes, steppers and Remove in the cart drawer and on /cart all hand their
     number to CartLines. Delegated on document, so it survives every redraw. */
  function initCartLines() {
    const SCOPE = 'cart-drawer, [data-cart-page-section]';
    const lineControl = (target, selector) => {
      const el = target && target.closest ? target.closest(selector) : null;
      return el && el.closest(SCOPE) ? el : null;
    };
    /* Cart markup from before this script (a page served during a theme update): lines only
       carry their position. One plain request, then a redraw. */
    const legacyChange = (line, quantity) => {
      const position = parseInt(line, 10);
      if (!position) return;
      cartRequest(T.routes.cartChange, { line: position, quantity, sections: cartSectionIds() })
        .then((cart) => renderCartSections(cart, cart.sections))
        .catch((error) => toast(error.message, 'error'));
    };

    /* A removal has just moved the list up under the finger: for half a second a tap that
       lands on any control of a cart line is ignored (a second tap in the same place used to
       remove or lower the next line). Capture phase, so it runs before the steppers. */
    document.addEventListener('click', (event) => {
      if (!CartLines.justShifted()) return;
      const item = event.target.closest ? event.target.closest('[data-line-item]') : null;
      if (item && item.closest(SCOPE)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    document.addEventListener('change', (event) => {
      const input = lineControl(event.target, '[data-line-qty]');
      if (!input) return;
      /* A cleared or invalid box means no change (the browser fires change before blur). */
      const n = parseInt(input.value, 10);
      if (!Number.isFinite(n)) { input.value = input.defaultValue; return; }
      if (!input.dataset.variant) { legacyChange(input.dataset.line, Math.max(0, n)); return; }
      CartLines.set(input.dataset.variant, Math.max(0, n), input.dataset.key);
    });

    document.addEventListener('click', (event) => {
      const remove = lineControl(event.target, '[data-line-remove]');
      if (!remove) return;
      event.preventDefault();
      if (!remove.dataset.variant) { legacyChange(remove.dataset.line, 0); return; }
      CartLines.set(remove.dataset.variant, 0, remove.dataset.key);
    });

    /* Enter in a quantity box applies the number; it must not submit the checkout form. */
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const input = lineControl(event.target, '[data-line-qty]');
      if (!input) return;
      event.preventDefault();
      input.blur();
    });

    /* Checkout tapped while a change is still on its way (a number typed in the drawer, a
       fast + tap): finish the change first, then go to checkout with the cart on screen.
       If the change failed, stay: the message is on screen, the numbers are the real ones
       again, and the shopper taps Checkout once more. */
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!form || !form.matches || !form.matches('#CartDrawerForm, [data-cart-page-section] form')) return;
      if (!CartLines.pending()) return;
      event.preventDefault();
      const inDrawer = form.id === 'CartDrawerForm';
      const button = event.submitter || form.querySelector('[name="checkout"]');
      const failedBefore = CartLines.failures();
      if (button) {
        button.classList.add('is-loading');
        button.disabled = true;
      }
      CartLines.settle().then(() => {
        const fresh = inDrawer ? document.getElementById('CartDrawerForm') : document.querySelector('[data-cart-page-section] form');
        if (CartLines.failures() !== failedBefore) {
          /* The old button may still be on the page when the redraw failed too. */
          [button].concat(fresh ? Array.from(fresh.querySelectorAll('[name="checkout"]')) : []).forEach((b) => {
            if (!b) return;
            b.disabled = false;
            b.classList.remove('is-loading');
          });
          return;
        }
        if (!fresh) return; /* the cart is empty now: nothing to check out */
        const go = document.createElement('input');
        go.type = 'hidden';
        go.name = 'checkout';
        go.value = '1';
        fresh.appendChild(go);
        fresh.submit();
      });
    });
  }

  /* The store never attaches text to a cart line. A link from outside can
     (/cart/add?id=...&properties[Promo]=any text), and Shopify would print that text in the
     cart and at checkout, which a scammer could use to show a fake payment instruction.
     The cart snippet does not print it and marks the line; here the text is blanked in the
     cart itself. Hidden (underscore) properties, which apps use, are kept. Switch:
     Theme settings > Cart > "Remove text added to cart lines by outside links". */
  async function cleanCartLineText() {
    if (T.settings.cleanLineText === false) return;
    if (!document.querySelector('[data-line-foreign]')) return;
    try {
      let cart = await fetchCart();
      let html = null;
      for (let round = 0; round < 10; round += 1) {
        const item = cart.items.find((i) => i.properties && Object.keys(i.properties).some((k) => k.charAt(0) !== '_' && i.properties[k] !== '' && i.properties[k] !== null));
        if (!item) break;
        const next = {};
        Object.keys(item.properties).forEach((k) => { next[k] = k.charAt(0) === '_' ? item.properties[k] : ''; });
        cart = await cartRequest(T.routes.cartChange, { id: item.key, quantity: item.quantity, properties: next, sections: cartSectionIds() });
        html = cart.sections || null;
      }
      await renderCartSections(cart, html);
    } catch (e) { /* the cart still works; the text is simply not printed by the theme */ }
  }

  /* Shopify's hCaptcha badge appears once a shopper touches a sign-up field and then stays
     fixed over the page (it covered the pack buttons). Show it only while a sign-up or
     contact form is on screen. */
  function initCaptchaBadge() {
    if (!('IntersectionObserver' in window)) return;
    const forms = document.querySelectorAll('form[action*="/contact"]');
    if (!forms.length) {
      document.body.classList.add('captcha-no-form');
      return;
    }
    const seen = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) seen.add(entry.target);
        else seen.delete(entry.target);
      });
      document.body.classList.toggle('captcha-in-view', seen.size > 0);
    });
    document.body.classList.add('captcha-watch');
    forms.forEach((form) => observer.observe(form));
  }

  function initShipBar() {
    const update = (cart) => {
      const threshold = T.settings.freeShippingThreshold || 0;
      document.querySelectorAll('[data-ship-bar]').forEach((bar) => {
        if (threshold <= 0) return;
        const remaining = Math.max(0, threshold - cart.total_price);
        const fill = bar.querySelector('.ship-bar__fill');
        const text = bar.querySelector('[data-ship-text]');

        if (fill) fill.style.width = `${Math.min(100, (cart.total_price / threshold) * 100)}%`;
        if (text) {
          // %%amount%% rather than a Liquid-looking token: the placeholder has to
          // survive being written into a Liquid attribute without being parsed.
          text.innerHTML =
            remaining === 0
              ? bar.dataset.unlockedText || 'You have unlocked free shipping.'
              : (bar.dataset.remainingText || "You're %%amount%% away from free shipping.").replace(
                  '%%amount%%',
                  `<strong>${moneyHtml(formatMoney(remaining).replace(/\.00$/, ''))}</strong>`
                );
          queueReconvert();
        }
      });
    };

    CartBus.subscribe(update);
  }

  /* --------------------------------------------------------------- ?pet= */

  /**
   * A dog-angle ad and a cat-angle ad send traffic to the same URL. This
   * reads ?pet=dog|cat once, swaps the hero heading to the matching copy,
   * and visually promotes the matching column in the species-split strip
   * (first on mobile, highlighted on desktop). No param = cat default,
   * since that's the locked fallback copy.
   */
  function initPetParam() {
    const params = new URLSearchParams(window.location.search);
    const pet = params.get('pet') === 'dog' ? 'dog' : 'cat';

    /* Only the dog link swaps the heading. The cat (default) heading is already
       rendered by the section with its highlight; rewriting it here used to
       print the raw "|" marker on the page. A "|" in the swapped text marks
       the highlighted phrase, the same rule the section uses. */
    const heading = document.querySelector('[data-pet-heading]');
    if (heading && pet === 'dog' && heading.dataset.headingDog) {
      const parts = heading.dataset.headingDog.split('|');
      heading.textContent = '';
      parts.forEach((part, i) => {
        const text = part.trim();
        if (!text) return;
        if (i === 1) {
          const mark = document.createElement('span');
          mark.className = 'mark';
          mark.textContent = text;
          heading.append(' ', mark, ' ');
        } else {
          heading.append(text);
        }
      });
    }

    document.querySelectorAll('[data-species-split]').forEach((strip) => {
      const cols = strip.querySelectorAll('[data-species]');
      cols.forEach((col) => {
        const isMatch = col.dataset.species === pet;
        col.classList.toggle('is-active', isMatch);
        col.setAttribute('data-species-order', isMatch ? '1' : '2');
      });
    });
  }

  /* The currency converter app (BUCKS) names its first entry "Auto Location" in every
     language. On the French site it reads "Détection automatique". The app draws its box
     after the page loads and may redraw it, so the label is fixed whenever the box changes. */
  function initCurrencyLabels() {
    if (!(document.documentElement.lang || '').toLowerCase().startsWith('fr')) return;
    const LABEL = 'Détection automatique';
    const fix = (box) => {
      box.querySelectorAll('li .bucksItem').forEach((item) => {
        if (item.textContent.trim() === 'Auto Location') item.textContent = LABEL;
      });
    };
    const watch = (box) => {
      fix(box);
      new MutationObserver(() => fix(box)).observe(box, { childList: true, subtree: true });
    };
    const ready = document.querySelector('.buckscc-currency-wrapper');
    if (ready) { watch(ready); return; }
    const waiter = new MutationObserver(() => {
      const box = document.querySelector('.buckscc-currency-wrapper');
      if (box) { waiter.disconnect(); watch(box); }
    });
    waiter.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => waiter.disconnect(), 20000);
  }

  /* ------------------------------------------------------------------ boot */

  function init() {
    initHeaderStuck();
    initReveal();
    initExternalLinks();
    initBundleSelector();
    initJuiceCheckout();
    initOfferCheckout();
    initShipBar();
    initCartLines();
    initCaptchaBadge();
    cleanCartLineText();
    initBackForwardReset();
    initBuyZoneFlag();
    initWhatsAppOrder();
    initLanguageForm();
    initPetParam();
    initCurrencyLabels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run behaviors when the theme editor swaps a section.
  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initExternalLinks();
    initBundleSelector();
    initJuiceCheckout();
    initOfferCheckout();
    initHeaderStuck();
  });

  document.addEventListener('shopify:section:select', (event) => {
    const drawer = event.target.querySelector('cart-drawer');
    if (drawer) drawer.open();
  });
})();
